/**
 * Utilidades para el contexto de análisis del módulo de Informes IA
 * Incluye validaciones, transformaciones y funciones auxiliares
 */

// Tipos de análisis disponibles
export const ANALYSIS_TYPES = [
  {
    value: "costs",
    label: "Costos / facturación",
  },
  {
    value: "consumption",
    label: "Consumos y variables eléctricas",
  },
  {
    value: "energy_matrix",
    label: "Matriz energética",
  },
  {
    value: "calculated_parameter",
    label: "Parámetros calculados",
  },
];

// Métricas básicas disponibles para consumo
export const BASIC_METRICS = [
  {
    value: "active_energy",
    label: "Energía activa",
  },
  {
    value: "reactive_energy",
    label: "Energía reactiva",
  },
  {
    value: "active_power",
    label: "Potencia activa",
  },
  {
    value: "reactive_power",
    label: "Potencia reactiva",
  },
  {
    value: "voltage",
    label: "Tensión",
  },
  {
    value: "current",
    label: "Corriente",
  },
  {
    value: "power_factor",
    label: "Factor de potencia",
  },
];

/**
 * Determina qué campos deben mostrarse según el tipo de análisis
 * @param {string} analysisType - El tipo de análisis seleccionado
 * @returns {Object} Configuración de campos visibles
 */
export const getFieldsConfigByAnalysisType = (analysisType) => {
  const configs = {
    costs: {
      showBranch: true,
      showDevice: false,
      showMetric: false,
      showCalculatedParameter: false,
      periodMode: "month_only",
      description: "Análisis de costos y facturación por sucursal",
    },
    consumption: {
      showBranch: true,
      showDevice: true,
      showMetric: true,
      showCalculatedParameter: false,
      periodMode: "flexible",
      description: "Análisis de consumos y variables eléctricas",
    },
    energy_matrix: {
      showBranch: true,
      showDevice: false,
      showMetric: false,
      showCalculatedParameter: false,
      periodMode: "flexible",
      description: "Análisis de matriz energética",
    },
    calculated_parameter: {
      showBranch: true,
      showDevice: true,
      showMetric: false,
      showCalculatedParameter: true,
      periodMode: "flexible",
      description: "Análisis de parámetros calculados",
    },
  };

  return configs[analysisType] || {};
};

/**
 * Valida que el contexto sea completo según las reglas de cada tipo de análisis
 * @param {Object} context - El contexto a validar
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateAnalysisContext = (context) => {
  const errors = [];

  if (!context.analysisType) {
    errors.push("Debes seleccionar un tipo de análisis");
    return { isValid: false, errors };
  }

  // Validar período (requerido para todos)
  if (!context.period || !context.period.month) {
    errors.push("Debes seleccionar un período");
  }

  // Validaciones específicas por tipo
  switch (context.analysisType) {
    case "costs":
      // Período es requerido (ya validado arriba)
      break;

    case "consumption":
      if (!context.metric) {
        errors.push("Debes seleccionar una métrica para consumos");
      }
      break;

    case "energy_matrix":
      // Solo período requerido
      break;

    case "calculated_parameter":
      if (!context.calculatedParameter) {
        errors.push("Debes seleccionar un parámetro calculado");
      }
      break;

    default:
      errors.push("Tipo de análisis no reconocido");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Normaliza la fecha a formato MM-YYYY
 * @param {string|Date} date - La fecha a normalizar
 * @returns {string} Fecha en formato MM-YYYY
 */
export const normalizeDateToMonthYear = (date) => {
  if (!date) return "";

  if (typeof date === "string") {
    // Si ya está en formato MM-YYYY
    if (/^\d{2}-\d{4}$/.test(date)) {
      return date;
    }
    // Si es una fecha ISO o similar
    try {
      date = new Date(date);
    } catch {
      return "";
    }
  }

  if (!(date instanceof Date)) {
    return "";
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}-${year}`;
};

/**
 * Valida que una fecha sea válida
 * @param {string} dateStr - Fecha en formato MM-YYYY
 * @returns {boolean}
 */
export const isValidMonthYear = (dateStr) => {
  if (!dateStr || !/^\d{2}-\d{4}$/.test(dateStr)) {
    return false;
  }

  const [month, year] = dateStr.split("-");
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);

  return m >= 1 && m <= 12 && y > 1970 && y < 2100;
};

/**
 * Limpia el contexto según el tipo de análisis (elimina campos que no aplican)
 * @param {Object} context - Contexto actual
 * @param {string} newAnalysisType - Nuevo tipo de análisis
 * @returns {Object} Contexto limpiado
 */
export const cleanContextByAnalysisType = (context, newAnalysisType) => {
  const cleaned = { ...context };
  const config = getFieldsConfigByAnalysisType(newAnalysisType);

  // Limpiar campos que no aplican
  if (!config.showDevice) {
    cleaned.device = null;
  }
  if (!config.showMetric) {
    cleaned.metric = null;
  }
  if (!config.showCalculatedParameter) {
    cleaned.calculatedParameter = null;
  }

  // Ajustar período si es necesario
  if (config.periodMode === "month_only") {
    cleaned.period = {
      mode: "month",
      month: cleaned.period?.month || "",
      startDate: "",
      endDate: "",
    };
  }

  return cleaned;
};

/**
 * Transforma los parámetros básicos del dispositivo a un formato de opciones Select
 * @param {Array} basicParams - Parámetros básicos del dispositivo
 * @returns {Array} Opciones para Select
 */
export const transformBasicParametersToOptions = (basicParams) => {
  if (!Array.isArray(basicParams)) return [];

  return basicParams
    .filter((p) => p?.visible === 1)
    .map((p) => ({
      value: p?.tag || "",
      label: p?.name || p?.tag || "",
      id: p?.id,
    }));
};

/**
 * Transforma los parámetros calculados a opciones Select
 * @param {Array} calculatedParams - Parámetros calculados
 * @returns {Array} Opciones para Select
 */
export const transformCalculatedParametersToOptions = (calculatedParams) => {
  if (!Array.isArray(calculatedParams)) return [];

  const options = [];
  const seen = new Set();

  calculatedParams
    .filter((p) => p?.device_calculated_parameter?.visible === 1)
    .forEach((p) => {
      const id = getCalculatedParameterId(p);
      const label = p?.name || p?.device_calculated_parameter?.name || "";
      const tag = p?.tag || "";
      const key = `${tag}:${label}`;

      if (!label || seen.has(key)) return;
      seen.add(key);

      options.push({
        value: id || "",
        label,
        id,
        unit: p?.unit || p?.device_calculated_parameter?.unit || "",
        scope: p?.scope || p?.device_calculated_parameter?.scope || "",
      });
    });

  return options;
};

/**
 * Extrae el nombre legible de una rama/sucursal
 * @param {Object} branch - Objeto de rama desde Redux
 * @returns {string} Nombre legible
 */
export const getBranchDisplayName = (branch) => {
  if (!branch) return "";
  if (typeof branch === "string") return branch;

  // Intentar diferentes propiedades comunes
  return (
    branch.office_name ||
    branch.name ||
    branch.nombres ||
    branch.company_name ||
    ""
  );
};

/**
 * Extrae el nombre legible de un dispositivo
 * @param {Object} device - Objeto de dispositivo desde Redux
 * @returns {string} Nombre legible (sin MAC)
 */
export const getDeviceDisplayName = (device) => {
  if (!device) return "";
  if (typeof device === "string") return device;

  return device.name || device.nombre || device.label || device.mac || "";
};

/**
 * Extrae el ID de un parámetro calculado de manera robusta
 * @param {Object} param - Parámetro calculado
 * @returns {string|number} ID válido para la API
 */
export const getCalculatedParameterId = (param) => {
  if (!param) return null;

  // Si tiene device_calculated_parameter, usar su ID
  if (param.device_calculated_parameter?.id) {
    return param.device_calculated_parameter.id;
  }

  // Si no, usar el ID del parámetro
  return param.id || null;
};

/**
 * Formatea un período para mostrar al usuario
 * @param {Object} period - Objeto período
 * @returns {string} Texto formateado
 */
export const formatPeriodDisplay = (period) => {
  if (!period) return "";

  if (period.mode === "month") {
    const month = period.month ? String(period.month).padStart(2, "0") : "";
    const year = period.year ? String(period.year) : "";

    if (!month || !year) return "";

    const monthNames = {
      "01": "Enero",
      "02": "Febrero",
      "03": "Marzo",
      "04": "Abril",
      "05": "Mayo",
      "06": "Junio",
      "07": "Julio",
      "08": "Agosto",
      "09": "Septiembre",
      "10": "Octubre",
      "11": "Noviembre",
      "12": "Diciembre",
    };

    return `${monthNames[month] || month} ${year}`;
  }

  if (period.mode === "custom") {
    if (period.startDate && period.endDate) {
      return `${period.startDate} a ${period.endDate}`;
    }

    if (period.startDate) return `desde ${period.startDate}`;
    if (period.endDate) return `hasta ${period.endDate}`;
  }

  return "";
};